import { useEffect, useState } from "react";

import {
  ErroDeApi, carregarRecomendacoes, enviarFeedback,
  type Fila, type MotivoRejeicao, type Recomendacao,
} from "../api";
import { moedaCheia, pct, dataBr } from "../leitura.js";
import { PainelExplicacao } from "./PainelExplicacao";
import { Vazio } from "./Cartao";

/**
 * Caixa de entrada do Decision Center (doc 03, item 8).
 *
 * A tela não sabe a máquina de estados — ela só desenha os botões que vieram
 * em `acoes_disponiveis` e mostra o erro que o `svc-decision` mandar quando
 * uma transição é recusada. Isso não é estilo: é a mesma razão do ADR-05 (o
 * front não calcula), aplicada à regra de negócio em vez de à métrica. Se a
 * regra mudar, ela muda no contrato (`decisao-estados.json`) e aqui não muda
 * nada.
 */
const ROTULO_ACAO: Record<string, string> = {
  assumir: "Assumir",
  aprovar: "Aprovar",
  rejeitar: "Rejeitar",
  devolver: "Devolver",
  reabrir: "Reabrir",
};

export function CaixaDeDecisao() {
  const [fila, setFila] = useState<Fila | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [rejeitando, setRejeitando] = useState<string | null>(null);
  const [enviando, setEnviando] = useState<string | null>(null);

  function recarregar() {
    setCarregando(true);
    carregarRecomendacoes()
      .then((f) => { setFila(f); setErro(null); })
      .catch((e: unknown) => setErro(e instanceof Error ? e.message : "falha ao carregar a fila"))
      .finally(() => setCarregando(false));
  }

  useEffect(recarregar, []);

  async function agir(id: string, acao: string, opcoes: { motivo?: string; texto?: string } = {}) {
    setEnviando(id);
    try {
      await enviarFeedback(id, acao, opcoes);
      setRejeitando(null);
      recarregar();
    } catch (e) {
      setErro(e instanceof ErroDeApi ? (e.message || "transição recusada") : "falha ao enviar a decisão");
    } finally {
      setEnviando(null);
    }
  }

  const recomendacoes = fila?.recomendacoes ?? [];

  return (
    <div className={carregando ? "conteudo carregando" : "conteudo"} aria-busy={carregando}>
      {erro && (
        <div className="aviso" role="alert">
          <strong>Não foi possível concluir.</strong>
          <span>{erro}</span>
        </div>
      )}

      {!carregando && recomendacoes.length === 0 && (
        <Vazio>Nenhuma recomendação em aberto — a fila está zerada.</Vazio>
      )}

      <div className="fila-decisao">
        {recomendacoes.map((r) => (
          <CartaoRecomendacao
            key={r.id}
            r={r}
            motivos={fila?.motivos_rejeicao ?? []}
            expandido={expandido === r.id}
            rejeitando={rejeitando === r.id}
            enviando={enviando === r.id}
            onExpandir={() => setExpandido(expandido === r.id ? null : r.id)}
            onIniciarRejeicao={() => setRejeitando(r.id)}
            onCancelarRejeicao={() => setRejeitando(null)}
            onAgir={(acao, opcoes) => agir(r.id, acao, opcoes)}
          />
        ))}
      </div>
    </div>
  );
}

function CartaoRecomendacao({
  r, motivos, expandido, rejeitando, enviando,
  onExpandir, onIniciarRejeicao, onCancelarRejeicao, onAgir,
}: {
  r: Recomendacao;
  motivos: MotivoRejeicao[];
  expandido: boolean;
  rejeitando: boolean;
  enviando: boolean;
  onExpandir: () => void;
  onIniciarRejeicao: () => void;
  onCancelarRejeicao: () => void;
  onAgir: (acao: string, opcoes?: { motivo?: string; texto?: string }) => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [texto, setTexto] = useState("");
  const definicaoMotivo = motivos.find((m) => m.codigo === motivo);

  return (
    <article className="recomendacao">
      <header>
        <div>
          <span className="selo tom-neutro">{r.tipo.replace(/_/g, " ")}</span>
          <h3>{r.produto} · {r.filial}</h3>
        </div>
        <p className="valor">{moedaCheia(r.impacto_estimado.economia_brl)}</p>
      </header>

      <p className="problema">{r.problema}</p>
      <p className="narrativa">{r.narrativa}</p>

      <dl className="metricas">
        <div><dt>Confiança</dt><dd>{pct(r.confianca * 100)}</dd></div>
        <div><dt>Score</dt><dd>{r.score.toFixed(2)}</dd></div>
        <div><dt>Expira</dt><dd>{dataBr(r.expira_em)}</dd></div>
        {r.alcada && <div><dt>Alçada</dt><dd>{r.alcada}</dd></div>}
      </dl>

      <button type="button" className="ligacao" onClick={onExpandir}>
        {expandido ? "ocultar explicação" : "ver explicação"}
      </button>
      {expandido && <PainelExplicacao id={r.id} />}

      {rejeitando ? (
        <div className="form-rejeicao">
          <label>
            <span className="sr">Motivo</span>
            <select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
              <option value="">Selecione o motivo</option>
              {motivos.map((m) => (
                <option key={m.codigo} value={m.codigo}>{m.rotulo}</option>
              ))}
            </select>
          </label>
          {definicaoMotivo?.exige_texto && (
            <input
              type="text" placeholder="Descreva o motivo" value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          )}
          <div className="acoes">
            <button type="button" onClick={onCancelarRejeicao}>Cancelar</button>
            <button
              type="button" className="perigo" disabled={!motivo || enviando}
              onClick={() => onAgir("rejeitar", { motivo, texto: texto || undefined })}
            >
              Confirmar rejeição
            </button>
          </div>
        </div>
      ) : (
        <div className="acoes">
          {r.acoes_disponiveis.map((acao) =>
            acao === "rejeitar" ? (
              <button key={acao} type="button" className="perigo" disabled={enviando} onClick={onIniciarRejeicao}>
                {ROTULO_ACAO[acao] ?? acao}
              </button>
            ) : (
              <button key={acao} type="button" disabled={enviando} onClick={() => onAgir(acao)}>
                {ROTULO_ACAO[acao] ?? acao}
              </button>
            ),
          )}
        </div>
      )}
    </article>
  );
}
